import {
  BufferAttribute,
  BufferGeometry,
  Float32BufferAttribute,
  PlaneGeometry,
} from "three";
import { APERTURE_ARC_SEGMENTS, type AperturePolygon } from "./geometry";
import type { FrameShape } from "./types";

/**
 * Inclusive vertex range on the polygon. When `end < start` the arc wraps
 * across the end → 0 boundary (used for the "left cap" arc on stadium ovals
 * and the left-side edge on rectangles, both of which finish at the seam).
 */
export interface PerimeterArcRange {
  start: number;
  end: number;
}

/**
 * Frustum-shaped tube wall around the mirror cavity perimeter.
 *
 * Builds a closed surface from the front aperture ring (`poly * scaleFront` at
 * `zFront`) to the back ring (`poly * scaleBack` at `zBack`). Used to render
 * the user's center text on the inside of the cavity (text "printed on the
 * side of the mirror"), with the texture tiled around the perimeter (U) and
 * along Z (V).
 *
 * UVs are parameterized so:
 *   - U ∈ [0, 1] = arclength around the perimeter (front-ring metric, used at
 *     both Z ends so columns line up vertically along the wall).
 *   - V = 0 at the back ring, V = 1 at the front ring.
 *
 * To tile the texture, set `texture.wrapS/T = RepeatWrapping` and
 * `texture.repeat.set(uRepeats, vRepeats)` on the caller side.
 */
export function makePerimeterWallGeometry(
  poly: AperturePolygon,
  scaleFront: number,
  scaleBack: number,
  zFront: number,
  zBack: number,
  flipU = false,
): BufferGeometry {
  const n = poly.length;
  if (n < 3) return new PlaneGeometry(0.02, 0.02);

  const arcs: number[] = new Array(n + 1);
  arcs[0] = 0;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    const len = Math.hypot(
      b.x * scaleFront - a.x * scaleFront,
      b.y * scaleFront - a.y * scaleFront,
    );
    arcs[i + 1] = arcs[i] + len;
  }
  const total = Math.max(arcs[n], 1e-6);

  const positions: number[] = [];
  const uvs: number[] = [];
  // Polygon vertex XY (no scale) per geometry vertex — used by the RGB shader
  // (`USE_RGB_WHEEL_PERIM_XY`) so the hue is sampled from the **perimeter
  // angle**, identical to what the rail tube reports for the same polygon
  // vertex. Otherwise, the rail's mitered cross-section offset shifts world
  // XY at corners and the rail/text colors drift apart there.
  const perimXY: number[] = [];
  for (let i = 0; i <= n; i++) {
    const wrap = i % n;
    const px = poly[wrap].x;
    const py = poly[wrap].y;
    const u0 = arcs[i] / total;
    const u = flipU ? 1 - u0 : u0;
    positions.push(px * scaleFront, py * scaleFront, zFront);
    uvs.push(u, 1);
    perimXY.push(px, py);
    positions.push(px * scaleBack, py * scaleBack, zBack);
    uvs.push(u, 0);
    perimXY.push(px, py);
  }

  const indices: number[] = [];
  for (let i = 0; i < n; i++) {
    const fi = 2 * i;
    const bi = 2 * i + 1;
    const fj = 2 * (i + 1);
    const bj = 2 * (i + 1) + 1;
    indices.push(fi, bi, bj);
    indices.push(fi, bj, fj);
  }

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geo.setAttribute("aPerimXY", new Float32BufferAttribute(perimXY, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/**
 * Frustum-shaped tube wall over a subset of polygon arcs.
 *
 * Each arc maps U ∈ [0, 1] across its own arclength independently, so a tiled
 * texture (`texture.repeat.set(N, 1)`) lays N copies along every arc — the
 * caller picks N from each arc's perimeter so glyphs read at a consistent
 * world size on every side.
 *
 * Used by the wall-text path when the frame has a long/short distinction
 * (rectangle, vrect, hoval, voval): build one geometry for the long arcs and
 * another for the short arcs, each with its own text texture.
 */
export function makePerimeterArcWallGeometry(
  poly: AperturePolygon,
  arcs: PerimeterArcRange[],
  scaleFront: number,
  scaleBack: number,
  zFront: number,
  zBack: number,
  flipU = false,
): BufferGeometry {
  const n = poly.length;
  if (n < 2 || arcs.length === 0) return new PlaneGeometry(0.02, 0.02);

  const positions: number[] = [];
  const uvs: number[] = [];
  // See `makePerimeterWallGeometry` for the rationale behind aPerimXY.
  const perimXY: number[] = [];
  const indices: number[] = [];

  for (const arc of arcs) {
    const idx = collectArcIndices(arc, n);
    if (idx.length < 2) continue;

    const arcLengths: number[] = [0];
    for (let k = 1; k < idx.length; k++) {
      const a = poly[idx[k - 1]];
      const b = poly[idx[k]];
      const len = Math.hypot(
        (b.x - a.x) * scaleFront,
        (b.y - a.y) * scaleFront,
      );
      arcLengths.push(arcLengths[k - 1] + len);
    }
    const total = arcLengths[idx.length - 1];
    if (total <= 1e-6) continue;

    const baseVertex = positions.length / 3;
    for (let k = 0; k < idx.length; k++) {
      const v = poly[idx[k]];
      const u0 = arcLengths[k] / total;
      const u = flipU ? 1 - u0 : u0;
      positions.push(v.x * scaleFront, v.y * scaleFront, zFront);
      uvs.push(u, 1);
      perimXY.push(v.x, v.y);
      positions.push(v.x * scaleBack, v.y * scaleBack, zBack);
      uvs.push(u, 0);
      perimXY.push(v.x, v.y);
    }

    for (let k = 0; k < idx.length - 1; k++) {
      const fi = baseVertex + 2 * k;
      const bi = baseVertex + 2 * k + 1;
      const fj = baseVertex + 2 * (k + 1);
      const bj = baseVertex + 2 * (k + 1) + 1;
      indices.push(fi, bi, bj);
      indices.push(fi, bj, fj);
    }
  }

  if (positions.length === 0) return new PlaneGeometry(0.02, 0.02);

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geo.setAttribute("aPerimXY", new Float32BufferAttribute(perimXY, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/**
 * Merge several `makePerimeterWallGeometry` outputs (one per depth band) into
 * a single `BufferGeometry` with a per-vertex `aBandIdx` attribute. The
 * merged geometry inherits `position`, `uv`, `aPerimXY` from the inputs
 * (concatenated) and adds `aBandIdx` (float per vertex) so the shader can
 * sample a per-band uniform array (`uBandOpacities[…]`) for opacity without
 * duplicating draw calls per band.
 *
 * Why this exists: the LightStyle = "text" path used to render N (≈11)
 * separate `<mesh>`-es with N material instances and N draw calls. Profiling
 * showed the bar→text transition spent ~80–110 ms in React reconcile +
 * material setup + bloom mip work scaling with N. Collapsing to a single
 * mesh per (channel × mirrored?) drops draw calls to 2 and React fiber
 * count proportionally, which is the bulk of the bar→text first-paint cost.
 *
 * Inputs MUST be `makePerimeterWallGeometry`/`makePerimeterArcWallGeometry`
 * outputs (indexed, with `position` / `uv` / `aPerimXY`). The cached band
 * geometries are NOT mutated — we read their attribute arrays and copy into
 * fresh typed arrays sized for the merged result.
 *
 * Returns `null` for an empty input list so callers can branch on visibility
 * (e.g. one channel may have only mirrored bands and no non-mirrored ones).
 */
export function mergeBandWallGeometries(
  bands: Array<{ wall: BufferGeometry; bandIdx: number }>,
): BufferGeometry | null {
  if (bands.length === 0) return null;

  let totalVerts = 0;
  let totalIndices = 0;
  for (const { wall } of bands) {
    const pos = wall.getAttribute("position");
    const idx = wall.getIndex();
    if (!pos || !idx) return null;
    totalVerts += pos.count;
    totalIndices += idx.count;
  }
  if (totalVerts === 0 || totalIndices === 0) return null;

  const positions = new Float32Array(totalVerts * 3);
  const uvs = new Float32Array(totalVerts * 2);
  const perimXYs = new Float32Array(totalVerts * 2);
  const bandIdxs = new Float32Array(totalVerts);
  // Uint32 index buffer because totalVerts can exceed 65535 with many bands ×
  // long perimeters; three.js auto-detects from the buffer attribute itemSize.
  const indices = new Uint32Array(totalIndices);

  let posWrite = 0;
  let uvWrite = 0;
  let perimWrite = 0;
  let bandWrite = 0;
  let idxWrite = 0;
  let vertexBase = 0;

  for (const { wall, bandIdx } of bands) {
    const pos = wall.getAttribute("position") as BufferAttribute;
    const uv = wall.getAttribute("uv") as BufferAttribute;
    const perim = wall.getAttribute("aPerimXY") as BufferAttribute;
    const idx = wall.getIndex()!;
    const vCount = pos.count;

    positions.set(pos.array as Float32Array, posWrite);
    posWrite += pos.array.length;
    uvs.set(uv.array as Float32Array, uvWrite);
    uvWrite += uv.array.length;
    perimXYs.set(perim.array as Float32Array, perimWrite);
    perimWrite += perim.array.length;

    for (let i = 0; i < vCount; i++) {
      bandIdxs[bandWrite++] = bandIdx;
    }

    const srcIdx = idx.array as ArrayLike<number>;
    for (let i = 0; i < idx.count; i++) {
      indices[idxWrite++] = srcIdx[i] + vertexBase;
    }
    vertexBase += vCount;
  }

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geo.setAttribute("aPerimXY", new Float32BufferAttribute(perimXYs, 2));
  geo.setAttribute("aBandIdx", new Float32BufferAttribute(bandIdxs, 1));
  geo.setIndex(new BufferAttribute(indices, 1));
  geo.computeVertexNormals();
  return geo;
}

function collectArcIndices(arc: PerimeterArcRange, n: number): number[] {
  const out: number[] = [];
  if (arc.end >= arc.start) {
    for (let i = arc.start; i <= arc.end; i++) out.push(i);
  } else {
    for (let i = arc.start; i < n; i++) out.push(i);
    for (let i = 0; i <= arc.end; i++) out.push(i);
  }
  return out;
}

/** Total length (after `scale`) of a single arc on the polygon. */
export function polygonArcLength(
  poly: AperturePolygon,
  arc: PerimeterArcRange,
  scale = 1,
): number {
  const n = poly.length;
  if (n < 2) return 0;
  const idx = collectArcIndices(arc, n);
  let total = 0;
  for (let k = 1; k < idx.length; k++) {
    const a = poly[idx[k - 1]];
    const b = poly[idx[k]];
    total += Math.hypot((b.x - a.x) * scale, (b.y - a.y) * scale);
  }
  return total;
}

/** Sum of multiple arcs. */
export function polygonArcsLength(
  poly: AperturePolygon,
  arcs: PerimeterArcRange[],
  scale = 1,
): number {
  let total = 0;
  for (const a of arcs) total += polygonArcLength(poly, a, scale);
  return total;
}

/**
 * Classify perimeter vertex ranges by long-side vs short-side.
 *
 * Returns `null` for shapes without a long/short distinction (`square`,
 * `circle`); callers fall back to the unified-perimeter wall geometry.
 *
 * Index layout matches `buildApertureHull` exactly:
 *   - Rectangle / vrect: 4 vertices CCW starting at (-hw, -hh).
 *     edges: 0→1 bottom, 1→2 right, 2→3 top, 3→0 left.
 *   - Stadium (hoval/voval): 2N+2 vertices (N = APERTURE_ARC_SEGMENTS = 32).
 *     [0,1] and [N+1,N+2] are the two straight edges (long axis); the cap
 *     arcs lie on [1..N+1] and [N+2..0] (short axis / curved sides).
 */
export function classifyPerimeterSides(
  shape: FrameShape,
  w: number,
  h: number,
): { long: PerimeterArcRange[]; short: PerimeterArcRange[] } | null {
  if (shape === "square" || shape === "circle") return null;

  if (shape === "rectangle" || shape === "vrect") {
    const horizontalIsLong = w >= h;
    const horizontalEdges: PerimeterArcRange[] = [
      { start: 0, end: 1 },
      { start: 2, end: 3 },
    ];
    const verticalEdges: PerimeterArcRange[] = [
      { start: 1, end: 2 },
      { start: 3, end: 0 },
    ];
    return horizontalIsLong
      ? { long: horizontalEdges, short: verticalEdges }
      : { long: verticalEdges, short: horizontalEdges };
  }

  if (shape === "hoval" || shape === "voval") {
    const N = APERTURE_ARC_SEGMENTS;
    const straights: PerimeterArcRange[] = [
      { start: 0, end: 1 },
      { start: N + 1, end: N + 2 },
    ];
    const caps: PerimeterArcRange[] = [
      { start: 1, end: N + 1 },
      { start: N + 2, end: 0 },
    ];
    return { long: straights, short: caps };
  }

  return null;
}

/** Total arclength around the polygon (after `scale`). */
export function polygonPerimeter(poly: AperturePolygon, scale = 1): number {
  const n = poly.length;
  if (n < 2) return 0;
  let total = 0;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    total += Math.hypot((b.x - a.x) * scale, (b.y - a.y) * scale);
  }
  return total;
}

/**
 * LED rail tube along the polygon perimeter at a fixed `z` plane.
 *
 * Hand-built with a mitered cross-section per polygon vertex instead of
 * `TubeGeometry` over a `CurvePath`: at sharp corners (rectangle 90°), the
 * curve-path approach makes Frenet frames flip discontinuously, twisting the
 * tube into a visible kink. Here every cross-section is a circle in the
 * (outward bisector ⊥ Z) plane at each vertex; the outward axis is scaled
 * by `1/cos(half-bend)` so perpendicular thickness stays constant on both
 * adjacent edges and the seam runs cleanly through the corner.
 *
 * Densely-sampled hulls (oval / circle, 32+ segments per arc) end up with
 * tiny per-vertex bends → miter factor ≈ 1 → visually identical to a smooth
 * tube. Only the rectangle / vrect corners benefit from the mitering, but
 * the same code path serves both shapes.
 */
export function makePerimeterRailGeometry(
  poly: AperturePolygon,
  scale: number,
  z: number,
  radius: number,
): BufferGeometry {
  const n = poly.length;
  if (n < 3) return new PlaneGeometry(0.02, 0.02);

  const verts2d = poly.map((p) => ({ x: p.x * scale, y: p.y * scale }));

  const edgeTan: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const a = verts2d[i];
    const b = verts2d[(i + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    edgeTan.push({ x: dx / len, y: dy / len });
  }

  // Polygon winding: signed area > 0 → CCW, outward normal of edge (tx, ty)
  // is (ty, -tx). For CW it flips. Apertures from `buildApertureHull` are
  // CCW, but compute it explicitly so the function works for either.
  let signedArea = 0;
  for (let i = 0; i < n; i++) {
    const a = verts2d[i];
    const b = verts2d[(i + 1) % n];
    signedArea += a.x * b.y - b.x * a.y;
  }
  const ccw = signedArea > 0;

  // Per-vertex outward miter direction (in XY) + scale so perpendicular
  // thickness across each adjacent edge equals `radius`.
  const miter: { x: number; y: number; factor: number }[] = [];
  for (let i = 0; i < n; i++) {
    const inT = edgeTan[(i + n - 1) % n];
    const outT = edgeTan[i];
    const bx = inT.x + outT.x;
    const by = inT.y + outT.y;
    const blen = Math.hypot(bx, by) || 1;
    const mx = (ccw ? by : -by) / blen;
    const my = (ccw ? -bx : bx) / blen;
    const dot = inT.x * outT.x + inT.y * outT.y;
    const cosHalf = Math.sqrt(Math.max(1e-3, (1 + dot) / 2));
    // Cap at 4 so a near-180° fold doesn't spike the miter into the scene.
    miter.push({ x: mx, y: my, factor: Math.min(4, 1 / cosHalf) });
  }

  const radialSegments = 8;
  const ringSize = radialSegments + 1;
  const positions: number[] = [];
  const indices: number[] = [];
  // Per-vertex polygon-vertex XY (without scale, without cross-section offset).
  // Used by the RGB shader (`USE_RGB_WHEEL_PERIM_XY`) so the rail's hue is
  // sampled at the perimeter angle and matches `makePerimeterWallGeometry`'s
  // hue at the same vertex — the rail and the wall text colors stay in lock
  // step even at sharp corners where the mitered cross-section displaces
  // world XY off the perimeter.
  const perimXY: number[] = [];

  for (let i = 0; i < n; i++) {
    const c = verts2d[i];
    const m = miter[i];
    // Polygon vertex without `scale` so it matches the wall-geometry
    // attribute (which is also unscaled). atan2 is invariant to uniform
    // positive scaling so this is fine for hue purposes.
    const polyX = poly[i].x;
    const polyY = poly[i].y;
    for (let j = 0; j <= radialSegments; j++) {
      const theta = (j / radialSegments) * Math.PI * 2;
      const cosT = Math.cos(theta);
      const sinT = Math.sin(theta);
      const inPlane = radius * cosT * m.factor;
      positions.push(
        c.x + inPlane * m.x,
        c.y + inPlane * m.y,
        z + radius * sinT,
      );
      perimXY.push(polyX, polyY);
    }
  }

  for (let i = 0; i < n; i++) {
    const next = (i + 1) % n;
    for (let j = 0; j < radialSegments; j++) {
      const a = i * ringSize + j;
      const b = i * ringSize + (j + 1);
      const c = next * ringSize + (j + 1);
      const d = next * ringSize + j;
      indices.push(a, b, c, a, c, d);
    }
  }

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.setAttribute("aPerimXY", new Float32BufferAttribute(perimXY, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}
